import api from './axiosConfig';

// Onboarding de usuarios de un cliente (épica SDS-31).
// No confundir con el onboarding de leads/SDR, que es otro dominio.

const MENSAJE_GENERICO = 'No pudimos verificar tus datos. Probá de nuevo en un rato.';

// El error de los endpoints públicos del registro por código llega con el número de
// soporte adentro: la pantalla siempre tiene a dónde mandar al usuario.
const respuestaDeError = (err) => ({
  ok: false,
  code: err?.response?.data?.code || null,
  mensaje: err?.response?.data?.error || MENSAJE_GENERICO,
  soporte: err?.response?.data?.soporte_whatsapp || null,
});

const onboardingUsuarioService = {
  invitar: async (profileId, { reenvio = false } = {}) => {
    const response = await api.post(
      `/onboarding-usuarios/${encodeURIComponent(profileId)}/invitar`,
      { reenvio },
    );
    return response.data;
  },

  // Tipo de acceso y primeros pasos. El backend lo rechaza (400) si ya se lo invitó.
  configurar: async (profileId, { tipo_acceso, acciones_configuradas }) => {
    const response = await api.put(
      `/onboarding-usuarios/${encodeURIComponent(profileId)}/configuracion`,
      { tipo_acceso, acciones_configuradas },
    );
    return response.data;
  },

  getEstado: async (profileId) => {
    const response = await api.get(`/onboarding-usuarios/${encodeURIComponent(profileId)}`);
    return response.data;
  },

  listarPorEmpresa: async (empresaId) => {
    const response = await api.get(`/onboarding-usuarios/empresa/${encodeURIComponent(empresaId)}`);
    return response.data;
  },

  // Embudo del proceso (#373). `desde`/`hasta` acotan a quiénes se invitó en ese período;
  // los pasos que dieron después se cuentan igual, aunque caigan fuera del rango.
  getEmbudo: async ({ desde, hasta, empresaId } = {}) => {
    const params = {};
    if (desde) params.desde = new Date(desde).toISOString();
    if (hasta) {
      // El selector de fechas devuelve el día a la medianoche: sin esto, "hasta hoy"
      // dejaría afuera a todos los invitados de hoy.
      const fin = new Date(hasta);
      fin.setHours(23, 59, 59, 999);
      params.hasta = fin.toISOString();
    }
    if (empresaId) params.empresaId = empresaId;

    const response = await api.get('/onboarding-usuarios/metricas/embudo', { params });
    return response.data;
  },

  // Registro por link personal (#368). Endpoints públicos: el usuario todavía no tiene cuenta.

  // Precarga de la pantalla: { nombre, email }. `null` = link inválido, ya usado o
  // reemplazado por un reenvío; la pantalla manda al login en vez de mostrar un error.
  getPrecargaRegistro: async (code) => {
    try {
      const response = await api.get(`/onboarding-usuarios/registro/${encodeURIComponent(code)}`);
      return response.data;
    } catch (err) {
      if (err?.response?.status === 404) return null;
      throw err;
    }
  },

  // Devuelve { resultado: 'registrado' | 'cuenta_existente', email }.
  completarRegistro: async (code, password, email) => {
    const response = await api.post(
      `/onboarding-usuarios/registro/${encodeURIComponent(code)}`,
      { password, ...(email ? { email } : {}) },
    );
    return response.data;
  },

  // Registro por código de la empresa (#371): el respaldo para el que nunca recibió el
  // link. Los dos endpoints son públicos y responden 404 con el mismo cuerpo cuando los
  // datos no identifican a nadie: la pantalla nunca sabe si el código existe.

  // { ok: true, nombre, email } | { ok: false, mensaje, soporte }
  identificarPorCodigoEmpresa: async ({ codigo, email, telefono }) => {
    try {
      const { data } = await api.post('/onboarding-usuarios/codigo-registro/identificar', {
        codigo_registro: codigo,
        email,
        telefono,
      });
      return { ok: true, nombre: data?.nombre || '', email: data?.email || email };
    } catch (err) {
      return respuestaDeError(err);
    }
  },

  // { ok: true, resultado: 'registrado' | 'cuenta_existente', email } | { ok: false, ... }
  registrarPorCodigoEmpresa: async ({ codigo, email, telefono, password }) => {
    try {
      const { data } = await api.post('/onboarding-usuarios/codigo-registro/registro', {
        codigo_registro: codigo,
        email,
        telefono,
        password,
      });
      return { ok: true, resultado: data?.resultado, email: data?.email || email };
    } catch (err) {
      return respuestaDeError(err);
    }
  },

  // Lectura sin efecto: devuelve el código si la empresa ya lo tiene, null si no.
  // El código no viaja en el payload de empresa, así que se pide por acá.
  verCodigoRegistroEmpresa: async (empresaId) => {
    try {
      const { data } = await api.get(
        `/onboarding-usuarios/empresa/${encodeURIComponent(empresaId)}/codigo-registro`,
      );
      return data?.codigo_registro || null;
    } catch (err) {
      return null;
    }
  },

  // Código que Soporte le dicta al usuario. Se genera la primera vez que se pide y
  // después no cambia. Solo admin de Sorby.
  getCodigoRegistroEmpresa: async (empresaId) => {
    const { data } = await api.post(
      `/onboarding-usuarios/empresa/${encodeURIComponent(empresaId)}/codigo-registro`,
    );
    return data?.codigo_registro || null;
  },

  marcarAvisoBienvenidaVisto: async () => {
    const response = await api.post('/onboarding-usuarios/aviso-bienvenida/visto');
    return response.data;
  },
};

export default onboardingUsuarioService;
